import { memoize } from "lodash";
import { get as getPathValue } from "lodash";
import type {
  BaseBlock,
  ChildrenProp,
} from "@circle-react-shared/BuilderUI/DataModel.types";
import { cloneWithNewIDs } from "@circle-react-shared/BuilderUI/Helpers/cloneWithNewIDs";
import { isChildrenProp } from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import type { ListPolicies } from "../types";

export const getRelativePath = memoize((path: string) =>
  path.replace(/^blocks\./, ""),
);

export const getParentArrayPath = memoize((path: string) =>
  path.replace(/\.\d+$/, ""),
);

export const batchUpdates = (updates: Array<() => void>) => {
  requestAnimationFrame(() => {
    updates.forEach(update => update());
  });
};

export const resolveInsertIndex = ({
  blocks,
  path,
  block,
  index,
  listPolicies,
}: {
  blocks: BaseBlock;
  path: string;
  block: BaseBlock | BaseBlock[];
  index: number | undefined;
  listPolicies: ListPolicies | undefined;
}): number => {
  const baseChildrenProp: unknown = getPathValue(blocks, path);
  if (!isChildrenProp(baseChildrenProp)) return index ?? 0;
  const childrenProp: ChildrenProp = baseChildrenProp;

  const policy = listPolicies?.[`${path}.value`];
  const defaultInsertionIndex = policy?.getDefaultInsertionIndex
    ? policy.getDefaultInsertionIndex({
        parentArray: childrenProp.value,
        parentPath: path,
        block,
      })
    : childrenProp.value.length;

  return index ?? defaultInsertionIndex;
};

export const insertClonedBlocks = ({
  childrenProp,
  insertIndex,
  block,
}: {
  childrenProp: ChildrenProp;
  insertIndex: number;
  block: BaseBlock | BaseBlock[];
}): void => {
  if (Array.isArray(block)) {
    const clonedBlocks = new Array(block.length);
    for (let i = 0; i < block.length; i++) {
      clonedBlocks[i] = cloneWithNewIDs(block[i]);
    }
    childrenProp.value.splice(insertIndex, 0, ...(clonedBlocks as BaseBlock[]));
    return;
  }
  childrenProp.value.splice(insertIndex, 0, cloneWithNewIDs(block));
};
